import axios from "axios";
import { handleRole } from "../Helper/handleRoleHelper";
import { showRateLimitError } from "../Helper/ShowAlert";

// `??` not `||`: in the docker build REACT_APP_API_URL is set to the empty
// string so axios resolves paths relative to the page origin and nginx
// proxies /api/* to the backend. `||` would discard "" and wrongly fall back
// to localhost:5200 in the container build.
export const Api_URI = process.env.REACT_APP_API_URL ?? "http://localhost:5200";

// ===========================================
// UTILITY FUNCTIONS
// ===========================================

export const api = (token) => {
  const axiosInstance = axios.create({
    baseURL: Api_URI,
    /* Atlas sits ~850ms RTT away, and the list endpoints make many sequential
       round trips, so getAllQuotes lands at 12-16s on a good run. At 19s a
       single slow moment turned the Email Queries page into "Failed to fetch
       quotes" — a blank screen rather than a slow one. Slow is survivable. */
    timeout: 45000,
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  // Add response interceptor to handle rate limiting errors (429)
  axiosInstance.interceptors.response.use(
    (response) => {
      // Return successful responses as-is
      return response;
    },
    (error) => {
      // Handle rate limit errors (429 Too Many Requests)
      if (error.response && error.response.status === 429) {
        const errorData = error.response.data || {};
        const retryAfterHeader = error.response.headers["retry-after"];

        // Parse retryAfter - could be number or string
        let retryAfter = errorData.retryAfter;
        if (!retryAfter && retryAfterHeader) {
          retryAfter = parseInt(retryAfterHeader, 10) || retryAfterHeader;
        }

        // Show rate limit error using Swal
        showRateLimitError({
          message: errorData.message || "Too many requests, please try again later.",
          retryAfter: typeof retryAfter === 'string' ? parseInt(retryAfter, 10) : retryAfter,
          limit: errorData.limit,
          window: errorData.window,
        });
      }

      // Return error to allow component-level error handling
      return Promise.reject(error);
    }
  );

  return axiosInstance;
};

// ===========================================
// AUTHENTICATION & USER MANAGEMENT
// ===========================================

export const loginUser = (data) => api(undefined).post("/api/login", data);
export const register = (data) => api(undefined).post("/api/register", data);
export const forgotPassword = (data) => api(undefined).post("/api/forgotPassword", data);
export const contact = (data) => api(undefined).post("/api/contact", data);

// User Profile Management
export const getUserProfile = (data) =>
  api(data.token)
    .get(`/api/${handleRole(data.role)}/myProfile`)
    .then((data) => data.data.users);

export const getAdminProfile = (data) =>
  api(data.token)
    .get(`/api/${handleRole(data.role)}/myProfile`)
    .then((data) => data.data.users);

export const editUserProfile = (data) =>
  api(data.token)
    .put(`/api/${handleRole(data.role)}/myProfile`, data.data)
    .then((data) => data);

export const editAdminProfile = (data) =>
  api(data.token)
    .put(`/api/${handleRole(data.role)}/myProfile`, data.data)
    .then((data) => data);

export const updateUserPassword = (data) =>
  api(data.token).patch(`/api/${handleRole(data.role)}/myProfileChangePassword`, data.data);

export const updateAdminPassword = (data) =>
  api(data.token).patch(`/api/${handleRole(data.role)}/myProfileChangePassword`, data.data);

// Fetch a single user (admin/operator) by ID
export const getThisUserById = (data) =>
  api(data.token)
    .get(`/api/${handleRole(data.role)}/user/${data.userId}`)
    .then((res) => res.data);

// Fetch a single agent by ID
export const getThisAgentById = (data) =>
  api(data.token)
    .get(`/api/${handleRole(data.role)}/getThisAgent?field=_id&data=${data.agentId}`)
    .then((res) => res.data);

// User Management
// Params go through axios `params` with explicit defaults — NEVER string
// interpolation. Callers such as TransportConversation omit search/searchOnField,
// and `?search=${undefined}` ships the literal text "undefined", which the
// backend treats as a real search term and answers with zero rows.
export const getAdminUsers = (data) =>
  data.query == "agent"
    ? api(data.token).get(`/api/${handleRole(data.role)}/getAllAgents`, {
      params: {
        type: data.type || "",
        page: data.page || 1,
        limit: data.limit || 10,
        search: data.search || "",
        searchOnField: data.searchOnField || "",
      },
    })
    : api(data.token).get(`/api/${handleRole(data.role)}/getUsers`, {
      params: {
        type: data.query || "",
        page: data.page || 1,
        limit: data.limit || 10,
        search: data.search || "",
        searchOnField: data.searchOnField || "",
      },
    });

export const editUsers = (data) =>
  data.data.role == "agent"
    ? api(data.token)
      .put(`/api/${handleRole(data.role)}/agent/` + data.id, data.data)
      .then((data) => data)
    : api(data.token)
      .put(`/api/${handleRole(data.role)}/user/` + data.id, data.data)
      .then((data) => data);

export const addAdmins = (data) =>
  data.data.role == "agent"
    ? api(data.token)
      .post(`/api/${handleRole(data.role)}/addNewAgent`, data.data)
      .then((data) => data)
    : api(data.token)
      .post(`/api/${handleRole(data.role)}/user`, data.data)
      .then((data) => data);

export const adminDeleteUsers = (data) =>
  api(data.token)
    .delete(`/api/${handleRole(data.role)}/user/` + data.id)
    .then((data) => data);

// Operator Email Assignment
export const getAllWatchedEmails = (token, role) =>
  api(token)
    .get(`/api/${handleRole(role)}/watched-emails`)
    .then((res) => res.data);

export const getAssignedEmails = (token, role, operatorId) =>
  api(token)
    .get(`/api/${handleRole(role)}/operators/${operatorId}/assigned-emails`)
    .then((res) => res.data);

export const assignEmailsToOperator = (token, role, operatorId, emails) =>
  api(token)
    .post(`/api/${handleRole(role)}/operators/${operatorId}/assign-emails`, { emails })
    .then((res) => res.data);

// ===========================================
// DASHBOARD & ANALYTICS
// ===========================================

export const getDashboardDetails = (data) =>
  api(data.token).get(
    `/api/${handleRole(data.role)}/dashboard?${data.queryString}`
  ).then((data) => data.data);

// ===========================================
// QUOTE MANAGEMENT
// ===========================================

export const createQuote = (data) =>
  api(data.loginToken)
    .post(`/api/${handleRole(data.role)}/createQuote`, data.payload)
    .then((res) => res.data);

export const buyQuote = (data) =>
  api(data.loginToken)
    .post(`/api/${handleRole(data.role)}/buyQuote?quoteId=${data.quoteId}`, data.payload)
    .then((res) => res.data);

export const verifyQuote = (data) =>
  api(data.loginToken)
    .get(`/api/${handleRole(data.role)}/verifyQuote?session=${data.transactionId}`)
    .then((res) => res.data);

export const getAllQuotes = (data) =>
  api(data.loginToken)
    .get(`/api/${handleRole(data.role)}/getAllQuotes`, {
      params: {
        type: data.type,
        page: data.page,
        limit: data.limit,
        search: data.search || "",
        searchOnField: data.searchOnField || "",
        flaggedOnly: data.flaggedOnly ? true : undefined,
        ...(data.source ? { source: data.source } : {}),
      },
    })
    .then((res) => res.data);

export const QuoteAirlineResponseListing = (data) =>
  api(data.loginToken)
    .get(`/api/${handleRole(data.role)}/getQuoteAirlineResponseListing`, {
      params: {
        page: data.page,
        limit: data.limit,
        search: data.search || "",
        searchOnField: data.searchOnField || "",
      },
    })
    .then((res) => res.data);

export const getAQuoteById = (data) =>
  api(data.loginToken)
    .get(`/api/${handleRole(data.role)}/getAQuote/${data.id}`)
    .then((res) => res.data);

export const updateQuoteStatus = (data) =>
  api(data.token)
    .put(`/api/${handleRole(data.role)}/updateAQuote/${data.quoteId}?action=respondToAQuote`, {
      status: data.status,
    })
    .then((res) => res.data);

export const updateTrackStatus = (data) =>
  api(data.token)
    .put(`/api/${handleRole(data.role)}/updateTrackStatus/${data.quoteId}`, {
      track_status: data.track_status,
    })
    .then((res) => res.data);

export const updateTrackingStages = (data) =>
  api(data.token)
    .put(`/api/${handleRole(data.role)}/trackingUpdates`, {
      stageId: data.stageId,
      status: data.status,
      quoteId: data.quoteId,
    })
    .then((res) => res.data);

export const getQuoteChecklistDetails = (data) =>
  api(data.token)
    .get(`/api/${handleRole(data.role)}/quote/${data.quoteId}/checklist`)
    .then((res) => res.data);

export const updateQuoteStageAndTasks = (data) =>
  api(data.token)
    .put(`/api/${handleRole(data.role)}/quote/${data.quoteId}/updateStageAndTasks`, data.data)
    .then((res) => res.data);

// P16 — snapshot the options as the customer will see them, at send time. The
// cost table is rebuilt from live rates on every load, so without this an
// inbound reply saying "option 2" cannot be resolved to anything.
// Persist the operator's cost-table working state (screening choice, margin,
// customs, extra, ticks) so reopening the table does not discard it.
export const saveCostTableDraft = (data) =>
  api(data.token)
    .put(`/api/${handleRole(data.role)}/quote/${data.id}/costTableDraft`, {
      rows: data.rows,
    })
    .then((res) => res.data);

/**
 * "I know the rate" options. A separate route from saveCostTableDraft on
 * purpose: that one strips every price server-side, because its rates stay
 * authoritative on the saved airline/agent. These options ARE the rate.
 */
export const saveAdHocOptions = (data) =>
  api(data.token)
    .put(`/api/${handleRole(data.role)}/quote/${data.id}/adHocOptions`, {
      options: data.options,
    })
    .then((res) => res.data);

export const recordSentOptions = (data) =>
  api(data.token)
    .put(`/api/${handleRole(data.role)}/quote/${data.id}/sentOptions`, {
      options: data.options,
    })
    .then((res) => res.data);

export const finalizeAQuoteNomination = (data) =>
  api(data.token)
    .put(`/api/${handleRole(data.role)}/quote/${data.id}/finalizeAQuoteNomination`, data.payload)
    .then(res => res.data);

// ===========================================
// VANT REFERENCES
// ===========================================

export const getAllVantRefs = (data) =>
  api(data.token)
    .get(`/api/${handleRole(data.role)}/getAllVantRefs`)
    .then((res) => res.data);

export const checkIfUserNameExists = (data) =>
  api(data.token)
    .get(`/api/${handleRole(data.role)}/checkIfUserNameExists?userName=${data.userName}`)
    .then((res) => res.data);

// ===========================================
// TAG MANAGEMENT
// ===========================================

export const getMyTags = (data) =>
  api(data.token)
    .get(`/api/${handleRole(data.role)}/tags`)
    .then((res) => res.data);

export const createTag = (data) =>
  api(data.token)
    .post(`/api/${handleRole(data.role)}/tags`, {
      name: data.name,
      color: data.color,
    })
    .then((res) => res.data);

export const updateTag = (data) =>
  api(data.token)
    .put(`/api/${handleRole(data.role)}/tags/${data.tagId}`, {
      name: data.name,
      color: data.color,
    })
    .then((res) => res.data);

export const deleteTag = (data) =>
  api(data.token)
    .delete(`/api/${handleRole(data.role)}/tags/${data.tagId}`)
    .then((res) => res.data);

export const assignTagToQuote = (data) =>
  api(data.token)
    .post(`/api/${handleRole(data.role)}/tags/${data.tagId}/assign/${data.quoteId}`)
    .then((res) => res.data);

export const removeTagFromQuote = (data) =>
  api(data.token)
    .delete(`/api/${handleRole(data.role)}/tags/${data.tagId}/remove/${data.quoteId}`)
    .then((res) => res.data);

export const getQuotesForTag = (data) =>
  api(data.token)
    .get(`/api/${handleRole(data.role)}/tags/${data.tagId}/quotes`, {
      params: {
        page: data.page || 1,
        limit: data.limit || 10,
      },
    })
    .then((res) => res.data);

export const getTagsByQuotes = (data) =>
  api(data.token)
    .post(`/api/${handleRole(data.role)}/tags/by-quotes`, {
      quoteIds: data.quoteIds || [],
    })
    .then((res) => res.data);

export const assignTagToQuotesBulk = (data) =>
  api(data.token)
    .post(`/api/${handleRole(data.role)}/tags/${data.tagId}/assign-bulk`, {
      quoteIds: data.quoteIds || [],
    })
    .then((res) => res.data);

export const falseFlagQuote = (data) =>
  api(data.token)
    .post(`/api/${handleRole(data.role)}/falseFlagQuote`, {
      quoteId: data.quoteId,
      confirmationText: data.confirmationText,
    })
    .then((res) => res.data);

// ===========================================
// AIRLINE MANAGEMENT
// ===========================================

// See getAdminUsers: interpolating absent params yielded "?search=undefined",
// which the backend read as a live filter and returned an empty airline list
// (AirlineConversation / AirlineRateRequestManager pass no search at all).
export const getAllAirlines = (data) =>
  api(data.token)
    .get(`/api/${handleRole(data.role)}/getAllAirlines`, {
      params: {
        search: data.search || "",
        searchOnField: data.searchOnField || "",
        page: data.page || 1,
        limit: data.limit || 10,
      },
    })
    .then((res) => res.data);

export const getThisAirline = (data) =>
  api(data.token)
    .get(
      `/api/${handleRole(data.role)}/getThisAirline?field=_id&data=` +
        encodeURIComponent(data.airlineId)
    )
    .then((res) => res.data);

export const updateThisAirline = (data) =>
  api(data.token)
    .post(`/api/${handleRole(data.role)}/updateThisAirline?airlineId=` + data.airlineId, {
      data: data.data,
    })
    .then((res) => res.data);

export const addNewAirline = (data) =>
  api(data.token)
    .post(`/api/${handleRole(data.role)}/addNewAirline`, { ...data.data })
    .then((res) => res.data);

export const bulkAddAwbStock = (data) =>
  api(data.token)
    .post(`/api/${handleRole(data.role)}/bulkAddAwbStock`, {
      airlineId: data.airlineId,
      numbers: data.numbers,
      expiresAt: data.expiresAt || null,
    })
    .then((res) => res.data);

export const getAwbStock = (data) =>
  api(data.token)
    .get(
      `/api/${handleRole(data.role)}/getAwbStock?airlineId=${data.airlineId}&page=${data.page || 1}&limit=${data.limit || 100}&status=${data.status || ""}&search=${encodeURIComponent(data.search || "")}`
    )
    .then((res) => res.data);

export const consumeAwbStock = (data) =>
  api(data.token)
    .post(`/api/${handleRole(data.role)}/consumeAwbStock`, {
      airlineId: data.airlineId,
      stockId: data.stockId,
      ref: data.ref || {},
    })
    .then((res) => res.data);

export const voidAwbStock = (data) =>
  api(data.token)
    .post(`/api/${handleRole(data.role)}/voidAwbStock`, {
      airlineId: data.airlineId,
      stockId: data.stockId,
      reason: data.reason || "",
    })
    .then((res) => res.data);

export const deleteAwbStock = (data) =>
  api(data.token)
    .post(`/api/${handleRole(data.role)}/deleteAwbStock`, {
      airlineId: data.airlineId,
      stockId: data.stockId,
    })
    .then((res) => res.data);

export const getSavedAgentsOnQuote = (data) =>
  api(data.token)
    .get(`/api/${handleRole(data.role)}/getSavedAgent?quoteId=${data.quoteId}`)
    .then((res) => res.data);

export const updateSavedAirlineRate = (data) =>
  api(data.token)
    .put(`/api/${handleRole(data.role)}/updateSavedAirlineRate`, {
      quoteId: data.quoteId,
      savedAirlineToUpdate: data.savedAirlineToUpdate,
      finalRate: data.finalRate,
      screeningPrices: data.screeningPrices,
      FFWH: data.FFWH,
      minCharge: data.minCharge,
      transitTime: data.transitTime,
      ...(data?.originAirports ? { originAirports: data.originAirports } : {}),
      // Both are read off the airline's reply by the extractor and were dropped
      // between the popover and the save, so no stored rate ever had an expiry
      // and every one was assumed to be sterling.
      ...(data?.currency ? { currency: data.currency } : {}),
      ...(data?.rateValidityUntil
        ? { rateValidityUntil: data.rateValidityUntil }
        : {}),
    })
    .then((res) => res.data);

export const updateMultipleSavedAirlines = (data) =>
  api(data.token)
    .put(`/api/${handleRole(data.role)}/updateSavedAirlines`, {
      quoteId: data.quoteId,
      saveAirlines: data.savedAirlines,
    })
    .then((res) => res.data);

export const updateAirlineThreadId = (data) =>
  api(data.token)
    .put(`/api/${handleRole(data.role)}/updateSavedAirlines`, {
      quoteId: data.quoteId,
      saveAirlines: [
        {
          airlineId: data.airlineId,
          emailThreadId: data?.threadId,
          richEmail: "",
        },
      ],
    })
    .then((res) => res.data);

// ===========================================
// AGENT MANAGEMENT
// ===========================================

export const updateSavedAgentFinalRate = (data) =>
  api(data.token)
    .put(`/api/${handleRole(data.role)}/updateSavedAgentFinalRate`, {
      quoteId: data.quoteId,
      savedAgentToUpdate: data.savedAgentToUpdate,
      minCharge: data.minCharge,
      finalRate: data.finalRate,
      screeningPrices: data.screeningPrices
    })
    .then((res) => res.data);

// export const updateMultipleSavedAgents = (data) =>
//   api(data.token)
//     .put(`/api/${handleRole(data.role)}/updateMultipleSavedAgents`, {
//       quoteId: data.quoteId,
//       savedAgentsToUpdate: data.savedAgents,
//     })
//     .then((res) => res.data);
export const updateMultipleSavedAgents = (data) =>
  api(data.token)
    .put(`/api/${handleRole(data.role)}/updateMultipleSavedAgents`, {
      quoteId: data.quoteId,
      savedAgentsToUpdate: data.savedAgents,
    })
    .then((res) => res.data);

export const createSavedAgent = (data) => {
  return api(data.token)
    .post(`/api/${handleRole(data.role)}/createSavedAgent`, data.payload)
    .then(res => res.data);
};

// ===========================================
// EMAIL & COMMUNICATION
// ===========================================

export const sendEmail = (data) => {
  const payload = {
    to: data.to,
    subject: data.subject,
    body: data.body,
    from: data.from,
    agentId: data.selectedAgentId,
    airlineId: data.selectedAirlineId,
    threadId: data.threadId,
    messageId: data.messageId,
    entityType: data.entityType,
    entityName: data.entityName,
    quoteId: data.quoteId,
  };

  if (data.cc && Array.isArray(data.cc) && data.cc.length > 0) {
    payload.cc = data.cc;
  }
  if (data.bcc && Array.isArray(data.bcc) && data.bcc.length > 0) {
    payload.bcc = data.bcc;
  }

  return api(data.token)
    .post(`/api/draft/send-email`, payload)
    .then((res) => res.data);
};

export const fetchEmailThread = (data) => {
  const skipInlineImages = data.skipInlineImages === true ? 'true' : 'false';
  // 30s hard cap. The backend has its own 25s race so we'll surface a useful
  // 504 before this trips; this is the belt-and-suspenders in case the
  // backend stalls mid-response.
  return api(data.token)
    .get(`/api/draft/thread/${data.threadId}?userEmail=${data.userEmail}&skipInlineImages=${skipInlineImages}`, {
      timeout: 30000,
    })
    .then((res) => res.data);
};

export const replyToEmailThread = (data) =>
  api(data.token)
    .post(`/api/${handleRole(data.role)}/email/reply`, {
      threadId: data.threadId,
      to: data.to,
      cc: data.cc || [],
      bcc: data.bcc || [],
      subject: data.subject,
      body: data.body,
      from: data.from,
      inReplyTo: data.inReplyTo,
      references: data.references,
      attachments: data.attachments || [],
    })
    .then((res) => res.data);

export const registerWatchedThread = (data) =>
  api(data.token)
    .post(`/api/${handleRole(data.role)}/register-thread`, {
      threadId: data.threadId,
    })
    .then((res) => res.data);

export const getOriginalEmailByThreadId = (data) =>
  api(data.loginToken)
    .get(`/api/${handleRole(data.role)}/originalEmail?threadId=${data.threadId}`)
    .then((res) => res.data);

export const downloadGmailAttachment = async (data) => {
  const { token, messageId, attachmentId, userEmail, role } = data;

  try {
    const res = await api(token)
      .get(`/api/${handleRole(role)}/attachment`, {
        params: { messageId, attachmentId, userEmail },
        responseType: "blob",
        timeout: 35000, // 35 second timeout (slightly longer than backend)
      });

    return res.data;
  } catch (error) {
    // If error response has data, try to parse it as JSON for error details
    if (error.response?.data && error.response.data instanceof Blob) {
      try {
        const text = await error.response.data.text();
        const errorData = JSON.parse(text);
        // Attach parsed error to response for better error handling
        error.response.data = errorData;
      } catch (parseError) {
        // If parsing fails, keep original blob response
      }
    }
    throw error;
  }
};

// ===========================================
// EMAIL TEMPLATES
// ===========================================

// Same defence as getAllAirlines/getAdminUsers: no raw interpolation. Most
// callers omit `purpose`, which used to ship as "undefined".
export const getListOfTemplates = (data) =>
  api(data.token)
    .get(`/api/${handleRole(data.role)}/email-templates`, {
      params: {
        for: data.for || "",
        purpose: data.purpose || "",
        search: data.search || "",
        searchOnField: data.searchOnField || "",
        ...(data.page ? { page: data.page } : {}),
        ...(data.limit ? { limit: data.limit } : {}),
      },
    })
    .then((res) => res.data);

export const emailTemplatePopulated = (data) =>
  api(data.token)
    .post(`/api/${handleRole(data.role)}/email/populated`, {
      quoteId: data.quoteId,
      templatePurpose: data.purpose,
      templateFor: data.for,
    })
    .then((res) => res.data);

export const createEmailTemplate = (data) =>
  api(data.token)
    .post(`/api/${handleRole(data.role)}/email/create`, data.payload)
    .then((res) => res.data);

export const getAllEmailTemplates = (data) =>
  api(data.token)
    .get(`/api/${handleRole(data.role)}/email-templates`, {
      params: {
        searchOnField: data.searchOnField || "",
        search: data.search || "",
        for: data.searchFor || "",
        page: data.page || 1,
        limit: data.limit || 10
      },
    })
    .then((res) => res.data);

export const getEmailTemplateAvailableReplacements = (data) =>
  api(data.token)
    .get(`/api/${handleRole(data.role)}/email-template/availableReplacements`)
    .then((res) => res.data);

export const updateEmailTemplate = (data) =>
  api(data.token)
    .put(`/api/${handleRole(data.role)}/email/update/${data.templateId}`, data.payload)
    .then((res) => res.data);

export const deleteEmailTemplate = (data) =>
  api(data.token)
    .delete(`/api/${handleRole(data.role)}/email/delete/${data.templateId}`)
    .then((res) => res.data);

// ===========================================
// EMAIL DRAFTS & RATE REQUESTS
// ===========================================

export const generateRateRequestEmail = (data) =>
  api(data.token)
    .get(
      `/api/draft/generateRateRequestEmail?containerId=${data.containerId}&for=${data.for}&purpose=${data.purpose}`
    )
    .then((res) => res.data);

export const saveDraftEmail = (data) => {
  const {
    token,
    quoteId,
    saveAirlines,
    saveAgents,
    type = "airline",
    savedAgentId,
    savedAirlineId,
  } = data;

  if (savedAgentId) {
    const [singleSavedAgentToUpdate] = saveAgents;
    return updateMultipleSavedAgents({
      token,
      role: data.role,
      quoteId,
      savedAgents: [{ ...singleSavedAgentToUpdate, agentId: savedAgentId }],
    });
  }

  if (savedAirlineId) {
    const [singleSavedAirlineToUpdate] = saveAirlines;
    return updateMultipleSavedAirlines({
      token,
      role: data.role,
      quoteId,
      savedAirlines: [{ ...singleSavedAirlineToUpdate, savedAirlineId }],
    });
  }

  if (type !== "airline") {
    return api(token)
      .post(`/api/${handleRole(data.role)}/connectAgentsToAQuote`, {
        quoteId,
        agentsToSave: [
          ...saveAgents.map(agent => ({ ...agent, airlineId: undefined, pricingCondition: "general" })),
        ],
      })
      .then((res) => res.data);
  }

  if (saveAirlines.length === 1) {
    const { airlineId, richEmail } = saveAirlines[0];

    return api(token)
      .post(`/api/${handleRole(data.role)}/saveDraftEmailForThisAirline`, {
        quoteId,
        airlineId,
        richEmail,
        pricingCondition: "general",
      })
      .then((res) => res.data);
  }

  return api(token)
    .post(`/api/${handleRole(data.role)}/saveDraftEmailForThisAirline`, {
      quoteId,
      saveAirlines,
    })
    .then((res) => res.data);
};

export const getSavedEmailDrafts = (data) =>
  api(data.token)
    .get(`/api/draft/getSavedAirlines`, {
      params: { quoteId: data.quoteId },
    })
    .then((res) => res.data);

// ===========================================
// SHIPPER MANAGEMENT
// ===========================================

export const updateShipperDetail = (data) =>
  api(data.token)
    .put(`/api/${handleRole(data.role)}/shipper/${data.shipperId}`, {
      shipperDetails: data?.shipperThreadId
        ? { shipperThreadId: data.shipperThreadId }
        : data.shipperDetails,
    })
    .then((res) => res.data);

export const updateShipperDetails = (data) =>
  api(data.token)
    .put(`/api/${handleRole(data.role)}/shipper/${data.shipperId}`, data.payload)
    .then((res) => res.data);

// ===========================================
// CUSTOMER MANAGEMENT
// ===========================================

export const updateCustomerDetails = (data) =>
  api(data.token)
    .put(`/api/${handleRole(data.role)}/quote/customerUpdate`, data.payload)
    .then((res) => res.data);

// ===========================================
// CONTAINER MANAGEMENT
// ===========================================

export const updateContainer = (data) =>
  api(data.token)
    .post(`/api/${handleRole(data.role)}/container/${data.containerId}`, data.payload)
    .then((res) => res.data);

// ===========================================
// NOTIFICATIONS
// ===========================================

export const getNotifications = (
  token,
  role,
  page = 1,
  limit = 10,
  search = "",
  filter = "all"
) =>
  api(token)
    .get(
      `/api/${handleRole(role)}/notifications?page=${page}&limit=${limit}&search=${encodeURIComponent(
        search
      )}&filter=${filter}`
    )
    .then((res) => res.data);

// Persist "Mark read" for the whole bell. The header used to dispatch a redux
// action only, so the badge returned in full on the next reload.
export const markAllNotificationsRead = (token, role) =>
  api(token)
    .put(`/api/${handleRole(role)}/notifications/read-all`)
    .then((res) => res.data);

export const markNotificationRead = (token, role, id) =>
  api(token)
    .put(`/api/${handleRole(role)}/notifications/${id}/read`)
    .then((res) => res.data);

// ===========================================
// TRANSACTIONS & PAYMENTS
// ===========================================

export const getAllTransactions = (data) =>
  api(data.token).get(
    `/api/${handleRole(data.role)}${data.transactionType}?limit=${data.limit}&page=${data.page
    }&search=${data.search}&searchOnField=${data.searchOnField}${data.sortByVal ? `&sortByVal=${data.sortByVal}` : ""
    }${data.ascending ? `&ascending=${data.ascending}` : ""}`
  );

export const getThisTransaction = (data) =>
  api(data.token).get(
    `/api/${handleRole(data.role)}${data.transactionType}/${data.transaction_id}`
  );

export const getOneTransaction = (data) =>
  api(data.token).get(
    `/api/${handleRole(data.role)}/getOneTransaction?transaction_id=${data.transaction_id}`
  );

export const getOneTransactionOfUser = (data) =>
  api(data.token).get(
    `/api/${handleRole(data.role)}/getOneTransaction?transaction_id=${data.transaction_id}`
  );

export const buyPoints = (data) =>
  api(data.token)
    .post(`/api/${handleRole(data.role)}/buyPoints`, data.data)
    .then((data) => data.data);

export const completeBuyPointsTransaction = (data) =>
  api(data.token)
    .get(
      `/api/${handleRole(data.role)}/buyPoints?trans_id=${data.sessionId}&status=${data.status}`,
      data.data
    )
    .then((data) => data.data);

// ===========================================
// CONTENT MANAGEMENT
// ===========================================

export const getAllContent = (data) =>
  api(data.token)
    .get(
      `/api/${handleRole(data.role)}/getAllContent?sortByVal=${data.sortByVal}&ascending=${data.ascending}`,
      data.data
    )
    .then((data) => data.data);

export const generateContent = (data) =>
  api(data.token)
    .post(`/api/${handleRole(data.role)}/generateContent`, data.data)
    .then((data) => data.data);

export const getFinalContent = (data) =>
  api(data.token)
    .get(
      `/api/${handleRole(data.role)}/getInitialContentAndUpdateEntry?taskId=${data.taskId}&content_id=${data.content_id}&service_name=${data.service_name}`,
      data.data
    )
    .then((data) => data.data);

export const buyContent = (data) =>
  api(data.token).post(`/api/${handleRole(data.role)}/buyContent?content_id=${data.content_id}`);

export const uploadContentAdmin = (data) =>
  api(data.token).post(`/api/${handleRole(data.role)}/uploadContentAdmin`, data.data, {
    headers: { "Content-Type": "multipart/form-data" },
  });

export const uploadAssetsUser = (data) =>
  api(data.token).post(
    `/api/${handleRole(data.role)}/uploadContentUser?task=` + data.task,
    data.data,
    { headers: { "Content-Type": "multipart/form-data" } }
  );

export const DownloadContentLink = async (data) => {
  return api()
    .get("api/downloadItem?link=" + data.link, {
      responseType: "blob",
    })
    .then((response) => {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "image.jpg");
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    })
    .catch((error) => {
      console.error("Error downloading image:", error);
    });
};

// ===========================================
// SERVICES MANAGEMENT
// ===========================================

export const getAllServices = (data) =>
  api(data.token)
    .get(`/api/${handleRole(data.role)}/getServices`)
    .then((data) => data.data);

export const getAllServicesForUser = (data) =>
  api(data.token)
    .get(
      `/api/${handleRole(data.role)}/getServices?service_type=${data.service_type
      }&isActive=${true}`,
      data.data
    )
    .then((data) => data.data);

export const createService = (data) =>
  api(data.token).post(`/api/${handleRole(data.role)}/createService`, data.data);

// ===========================================
// COMPANY & SUBSCRIPTION MANAGEMENT
// ===========================================

export const getUserCompany = (data) =>
  api(data.token)
    .get(`/api/${handleRole(data.role)}/myCompany`)
    .then((data) => data.data.company);

export const getPlanDetails = (data) =>
  api(data.token).get(
    `/api/${handleRole(data.role)}/getPlans` +
    (data.plan_type ? "?plan_type=" + data.plan_type : "")
  );

export const getMyCurrentSubscription = (data) =>
  api(data.token)
    .get(`/api/${handleRole(data.role)}/getMyCurrentSubscription`)
    .then((data) => data.data);

// ===========================================
// USER INTERESTS & ANALYTICS
// ===========================================

export const adminFetchUserContentInterest = (data) =>
  api(data.token)
    .get(`/api/${handleRole(data.role)}/getUserCompany?user_id=` + data.user_id)
    .then((data) => data.data);

export const userFetchContentInterest = (data) =>
  api(data.token)
    .get(`/api/${handleRole(data.role)}/getUserCompany`)
    .then((data) => data.data);

// ===========================================
// REAL-TIME COMMUNICATION
// ===========================================

export const setupSSEConnection = (token, role) => {
  const url = `${process.env.REACT_APP_API_URL ?? "http://localhost:5200"
    }/api/${handleRole(role)}/sse/setup?token=${token}`;

  const eventSource = new EventSource(url);

  eventSource.onopen = () => {
    console.log("SSE connection opened");
  };

  eventSource.onmessage = (event) => {
    console.log("SSE message received (default handler):", event.data);
  };

  eventSource.onerror = (error) => {
    console.error("SSE connection error:", error);
  };

  return eventSource;
};

// ===========================================
// DOCUMENT MANAGEMENT
// ===========================================

export const generateDocument = (data) =>
  api(data.token)
    .post(`/api/${handleRole(data.role)}/quote/${data.quoteId}/collection`, data.payload)
    .then((res) => res.data);

export const getGeneratedCollectionDocument = (data) =>
  api(data.token)
    .get(`/api/${handleRole(data.role)}/quote/${data.quoteId}/collection`)
    .then((res) => res.data);

export const generatePortalLinks = (data) =>
  api(data.token)
    .get(`/api/${handleRole(data.role)}/quote/${data.quoteId}/portalLinks`)
    .then((res) => res.data);

// Quote documents (HAWB, MAWB, Invoice, Service Invoice) — server-rendered
// HTML → puppeteer → PDF. See backend/app/services/documentService.js.
export const createQuoteDocument = (data) =>
  api(data.token)
    .post(`/api/${handleRole(data.role)}/quote/${data.quoteId}/documents`, data.payload)
    .then((res) => res.data);

export const listQuoteDocuments = (data) =>
  api(data.token)
    .get(`/api/${handleRole(data.role)}/quote/${data.quoteId}/documents`, {
      params: data.type ? { type: data.type } : {},
    })
    .then((res) => res.data);

export const getQuoteDocument = (data) =>
  api(data.token)
    .get(`/api/${handleRole(data.role)}/quote/${data.quoteId}/documents/${data.docId}`)
    .then((res) => res.data);

export const updateQuoteDocument = (data) =>
  api(data.token)
    .put(`/api/${handleRole(data.role)}/quote/${data.quoteId}/documents/${data.docId}`, data.payload)
    .then((res) => res.data);

export const deleteQuoteDocument = (data) =>
  api(data.token)
    .delete(`/api/${handleRole(data.role)}/quote/${data.quoteId}/documents/${data.docId}`)
    .then((res) => res.data);

export const previewDocumentHtml = (data) =>
  api(data.token)
    .post(
      `/api/${handleRole(data.role)}/quote/${data.quoteId}/documents/preview-html`,
      data.payload
    )
    .then((res) => res.data);

export const downloadDocumentPdf = (data) =>
  api(data.token)
    .get(`/api/${handleRole(data.role)}/quote/${data.quoteId}/documents/${data.docId}/pdf`, {
      responseType: "blob",
      params: data.copySet ? { copySet: data.copySet } : {},
      // A full copy set is many puppeteer-rendered pages; override the 19s
      // default so a slow render isn't aborted client-side into a false
      // "Couldn't export PDF" while the server is still producing a valid PDF.
      timeout: 120000,
    })
    .then((res) => res.data);

// ===========================================
// Test MANAGEMENT
// ===========================================

export const testApi = (data) =>
  api().get(`/api/verify`, {
    headers: {
      "X-Q-Id": data.id,
      ...((data.type && data.token) ? { [`X-Magic-Token-${data.type}`]: data.token } : {}),
      ...((data.type && data.type_id) ? { [`X-${data.type}-Id`]: data.type_id } : {}),
    }
  }).then(res => res.data)

// ===========================================
// STICKY NOTES MANAGEMENT
// ===========================================

export const getStickyNotes = (data) =>
  api(data.token)
    .get(`/api/${handleRole(data.role)}/stickyNote/${data.id}`)
    .then((res) => res.data);

export const upsertStickyNote = (data) =>
  api(data.token)
    .post(`/api/${handleRole(data.role)}/stickyNote/${data.id}`, data.data)
    .then((res) => res.data);

// ===========================================
// MEDIA MANAGEMENT
// ===========================================

export const uploadMedia = (data) => {
  const formData = new FormData();

  // Append files (can be multiple) - backend expects "files" field name
  if (Array.isArray(data.files)) {
    data.files.forEach((file) => {
      formData.append("files", file);
    });
  } else if (data.files) {
    formData.append("files", data.files);
  }

  // Append required fields
  formData.append("refId", data.refId);
  formData.append("modelName", data.modelName);

  return api(data.token)
    .post(`/api/media/upload`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    })
    .then((res) => res.data);
};

export const linkMedia = (data) =>
  api(data.token)
    .post(`/api/media/link/${data.mediaId}`)
    .then((res) => res.data);

export const verifyVerification = (data) =>
  api(data.token)
    .get(`/api/verification?mediaId=${data.mediaId}&notes=${data.notes}`)
    .then((res) => res.data);

export const getCollectionNote = (data) =>
  api(data.token)
    .get(`/api/collection-note`)
    .then((res) => res.data);

export const getAssets = (data) =>
  api(data.token)
    .get(`/api/assets`)
    .then((res) => res.data);

// ===========================================
// TENANT CONFIG
// ===========================================

export const getTenantConfig = (data) =>
  api(data.loginToken)
    .get(`/api/${handleRole(data.role)}/config`)
    .then((res) => res.data);

export const updateTenantConfig = (data) =>
  api(data.loginToken)
    .put(`/api/${handleRole(data.role)}/config`, data.payload)
    .then((res) => res.data);

// ===========================================
// MANUAL QUOTES
// ===========================================

export const createManualQuote = (data) =>
  api(data.loginToken)
    .post(`/api/${handleRole(data.role)}/manual-quote`, data.payload)
    .then((res) => res.data);

// AI-assisted extraction of a pasted message / screenshot into form fields.
// Accepts: { loginToken, role, text?, imageFile? }
// At least one of `text` / `imageFile` must be provided. Backend runs the
// gpt-4o-mini extractor and returns a frontend-shaped preview the operator can
// edit before applying to the main form. 60s timeout — vision calls are slower
// than plain chat completions.
export const parseShipmentFromInput = (data) => {
  const form = new FormData();
  if (data.text && data.text.trim()) form.append("text", data.text);
  if (data.imageFile) form.append("image", data.imageFile);
  if (data.quoteId) form.append("quoteId", String(data.quoteId));
  if (data.messageId != null && data.messageId !== "")
    form.append("messageId", String(data.messageId));
  if (data.force) form.append("force", "true");
  /* Point the extractor at an attachment instead of the body. Only a reference
     travels — the server resolves the Gmail ids itself from a fresh thread
     listing, so a client can never name which bytes it wants. */
  if (data.attachmentIndex != null && data.attachmentIndex !== "")
    form.append("attachmentIndex", String(data.attachmentIndex));
  if (data.attachmentFilename)
    form.append("attachmentFilename", String(data.attachmentFilename));
  // Airline/transport conversations are separate threads from the customer one.
  if (data.attachmentThreadId)
    form.append("attachmentThreadId", String(data.attachmentThreadId));
  return api(data.loginToken)
    .post(`/api/${handleRole(data.role)}/manual-quote/parse`, form, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 60000,
    })
    .then((res) => res.data);
};

/**
 * Operator-driven AI parse of an airline rate email. Mirrors
 * parseShipmentFromInput but targets a SavedAirline doc and returns
 * { data, wanted, pricingCondition, cached, tokensUsed }. `wanted` is the
 * rates entry matching the saved airline's pricingCondition — frontend uses it
 * to fill finalRate without re-deriving the choice.
 */
export const parseAirlineRateFromInput = (data) => {
  const form = new FormData();
  if (data.text && data.text.trim()) form.append("text", data.text);
  if (data.imageFile) form.append("image", data.imageFile);
  form.append("savedAirlineId", String(data.savedAirlineId));
  if (data.messageId != null && data.messageId !== "")
    form.append("messageId", String(data.messageId));
  if (data.force) form.append("force", "true");
  return api(data.loginToken)
    .post(`/api/${handleRole(data.role)}/parse-airline-rate`, form, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 60000,
    })
    .then((res) => res.data);
};

/**
 * Operator-driven AI parse of an agent (transporter) rate email. Returns
 * { data: { finalCost, currency, screeningPrices }, cached, tokensUsed }.
 */
export const parseAgentRateFromInput = (data) => {
  const form = new FormData();
  if (data.text && data.text.trim()) form.append("text", data.text);
  if (data.imageFile) form.append("image", data.imageFile);
  form.append("savedAgentId", String(data.savedAgentId));
  if (data.messageId != null && data.messageId !== "")
    form.append("messageId", String(data.messageId));
  if (data.force) form.append("force", "true");
  return api(data.loginToken)
    .post(`/api/${handleRole(data.role)}/parse-agent-rate`, form, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 60000,
    })
    .then((res) => res.data);
};

/** Per-message summary + suggested reply (not form-fill). */
export const postQuoteMessageAssist = (data) =>
  api(data.loginToken)
    .post(
      `/api/${handleRole(data.role)}/quote-queries/${encodeURIComponent(
        data.quoteId
      )}/messages/${encodeURIComponent(data.messageId)}/assist${
        data.force ? "?force=1" : ""
      }`,
      {
        mode: data.mode || "both",
        // Airline / transporter panes pass the entity-specific thread so the
        // backend looks for the message in that thread, not the quote's
        // customer thread. Customer/shipper omit it (falls back to quote's
        // gmailThreadId server-side).
        ...(data.threadId ? { threadId: data.threadId } : {}),
      },
      { timeout: 120000 }
    )
    .then((res) => res.data);

export const getAllManualQuotes = (data) =>
  api(data.loginToken)
    .get(`/api/${handleRole(data.role)}/getAllQuotes`, {
      params: {
        type: data.type,
        page: data.page,
        limit: data.limit,
        search: data.search || "",
        searchOnField: data.searchOnField || "",
        source: "manual",
      },
    })
    .then((res) => res.data);
